import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import { DeliveryListItem, DeliveryStatus } from './types';
import { formatUserName, formatTimeAmPm } from './perfectTruckUtils';
import { useTheme } from '../../theme';
import { useDarkStyles } from '../../theme/useDarkStyles';

const STATUS_CONFIG_LIGHT: Record<DeliveryStatus, { bg: string; color: string; label: string }> = {
  'Pending Check': { bg: AdminColors.yellow50, color: AdminColors.yellow200, label: 'Pending Check' },
  'In Progress': { bg: AdminColors.blue50, color: AdminColors.blue700, label: 'In Progress' },
  'Approved': { bg: AdminColors.green75, color: AdminColors.green600, label: 'Approved' },
  'Rejected': { bg: AdminColors.red75, color: AdminColors.red400, label: 'Rejected' },
  'In Progress - Certifier': { bg: AdminColors.blue50, color: AdminColors.blue700, label: 'In Progress - Certifier' },
  'In Progress - Checker': { bg: AdminColors.blue50, color: AdminColors.blue700, label: 'In Progress - Checker' },
  'Approved - No Certification': { bg: AdminColors.green75, color: AdminColors.green600, label: 'Approved - No Certification' },
  'Rejected - No Certification': { bg: AdminColors.red75, color: AdminColors.red400, label: 'Rejected - No Certification' },
};

const STATUS_CONFIG_DARK: Record<DeliveryStatus, { bg: string; color: string; label: string }> = {
  'Pending Check':          { bg: '#92600A', color: '#FFFFFF', label: 'Pending Check' },
  'In Progress':            { bg: '#1A5BB5', color: '#FFFFFF', label: 'In Progress' },
  'Approved':               { bg: '#1A6B24', color: '#FFFFFF', label: 'Approved' },
  'Rejected':               { bg: '#9B2626', color: '#FFFFFF', label: 'Rejected' },
  'In Progress - Certifier':{ bg: '#1A5BB5', color: '#FFFFFF', label: 'In Progress - Certifier' },
  'In Progress - Checker':  { bg: '#1A5BB5', color: '#FFFFFF', label: 'In Progress - Checker' },
};

interface Props {
  record: DeliveryListItem;
  onPress: (record: DeliveryListItem) => void;
  onViewPress?: (record: DeliveryListItem) => void;
}

const PerfectTruckRecordCard: React.FC<Props> = ({ record, onPress, onViewPress }) => {
  const { t } = useTranslation('perfectTruck');
  const styles = useDarkStyles(baseStyles);
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const statusConfig = isDark ? STATUS_CONFIG_DARK : STATUS_CONFIG_LIGHT;
  const cfg = statusConfig[record.status] ?? (isDark
    ? { bg: '#2B2B2B', color: '#B0B0B0', label: record.status }
    : { bg: AdminColors.gray50, color: AdminColors.gray450, label: record.status }
  );

  const formatStatusDateTime = (value: string | null | undefined): string => {
    if (!value) return '--';

    let normalized: string;
    // Time-only string from backend (e.g. "12:19:39") — combine with deliveryDate and treat as UTC
    if (!value.includes('T') && !value.includes('-') && value.includes(':')) {
      normalized = `${record.deliveryDate}T${value}Z`;
    } else if (value.includes('T') && !value.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(value)) {
      // ISO string with no timezone offset — backend sends UTC, so append Z
      normalized = `${value}Z`;
    } else {
      normalized = value;
    }

    const d = new Date(normalized);
    if (isNaN(d.getTime())) return '--';

    // Local date portion: "Jun 05, 2026"
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();

    // Reuse the same formatTimeAmPm the web table uses — gives "5:49 PM IST"
    const timePart = formatTimeAmPm(normalized);

    return `${month} ${day}, ${year}, ${timePart}`;
  };

  // Choose icon + sub-line text based on status
  let subIcon: string = 'schedule';
  let subText: string | null = null;
  if (record.status === 'Approved' && record.checkerName) {
    subIcon = 'check-circle';
    subText = `${t('record_card.checked_by')} ${formatUserName(record.checkerName)} ${t('record_card.label_at')} ${formatStatusDateTime(record.checkedAt)}`;
  } else if (record.status === 'Rejected' && record.checkerName) {
    subIcon = 'info-outline';
    subText = `${t('record_card.rejected_by')} ${formatUserName(record.checkerName)} ${t('record_card.label_at')} ${formatStatusDateTime(record.checkedAt)}`;
  } else if (record.certifierName) {
    subIcon = 'schedule';
    subText = `${t('record_card.certified_by')} ${formatUserName(record.certifierName)} ${t('record_card.label_at')} ${formatStatusDateTime(record.certifiedAt)}`;
  }

  return (
    <TouchableOpacity
      style={[styles.card, isDark && { backgroundColor: colors.background.surface }]}
      activeOpacity={0.7}
      onPress={() => onPress(record)}
    >
      <View style={styles.topRow}>
        <Text style={[styles.route, isDark && { color: colors.text.primary }]} numberOfLines={1}>{record.routeId}</Text>
        <View style={styles.topRowRight}>
          <View style={[styles.badge, { backgroundColor: cfg.bg }]}>
            <Text style={[styles.badgeText, { color: cfg.color }]}>{cfg.label || record.status}</Text>
          </View>
          {record.status === 'Pending Check' && onViewPress ? (
            <TouchableOpacity
              style={styles.eyeButton}
              onPress={() => onViewPress(record)}
              accessibilityLabel="View certification record"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <MaterialIcons name="visibility" size={18} color={isDark ? colors.brand.primary : AdminColors.blue700} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      <View style={styles.metaRow}>
        <Text style={[styles.metaText, isDark && { color: colors.text.secondary }]}>{record.routeType}</Text>
        <Text style={[styles.metaText, isDark && { color: colors.text.secondary }]}>Asset: {record.assetId}</Text>
      </View>

      {subText ? (
        <View style={styles.subRow}>
          <MaterialIcons name={subIcon} size={14} color={isDark ? colors.text.secondary : AdminColors.textMuted} />
          <Text style={[styles.subText, isDark && { color: colors.text.secondary }]} numberOfLines={1}>{subText}</Text>
        </View>
      ) : null}
    </TouchableOpacity>
  );
};

const baseStyles = StyleSheet.create({
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 14,
    marginBottom: 8,
    ...Platform.select({
      web: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } as any,
      default: {
        elevation: 1,
        shadowColor: '#000',
        shadowOpacity: 0.08,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 1 },
      },
    }),
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    gap: 8,
  },
  route: {
    flex: 1,
    fontFamily: FontFamily.bold,
    fontSize: 15,
    color: AdminColors.textPrimary,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  badgeText: {
    fontFamily: FontFamily.bold,
    fontSize: 10,
  },
  topRowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  eyeButton: {
    padding: 2,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metaText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
  },
  subText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
  },
});

export default PerfectTruckRecordCard;
